import { evaluate, type AstNode } from "./ExprEval";
import { Ternary } from "./Ternary";
import { tagValue, type Blackboard, type Listener } from "./Blackboard";
import type { EventBus } from "./EventBus";
import type { AchievementSink } from "./AchievementStore";

export interface AchievementDef {
  title: string;
  desc: string;
  icon?: string;
  hidden: boolean;
  cond_ast: AstNode;
  cond: string;
}

export class AchievementEngine {
  private _achievements: Map<string, AchievementDef> = new Map();
  private _unlocked: Set<string> = new Set();
  private _bb: Blackboard;
  private _bus: EventBus;
  private _store: AchievementSink;
  private _gameId: string;
  private _unsubBb: (() => void) | null = null;

  constructor(bb: Blackboard, bus: EventBus, store: AchievementSink, gameId: string) {
    this._bb = bb;
    this._bus = bus;
    this._store = store;
    this._gameId = gameId;
  }

  load(defs: Record<string, AchievementDef>): void {
    this._achievements = new Map(Object.entries(defs));

    // Unsubscribe any previous listener before registering a new one
    if (this._unsubBb) { this._unsubBb(); this._unsubBb = null; }

    if (this._achievements.size === 0) return;

    // Load persisted unlocks asynchronously — pre-write to BB when ready.
    // Merge rather than overwrite: with a server-backed sink this is a real
    // network round trip, so an achievement can legitimately already have
    // unlocked synchronously (via the BB listener below) before this
    // resolves — replacing `_unlocked` wholesale would silently re-fire it.
    this._store.loadUnlocked(this._gameId).then((unlocked) => {
      for (const id of unlocked) {
        this._unlocked.add(id);
        this._bb.set(`_achievement_${id}`, tagValue(true));
      }
      // Initial evaluation — may unlock achievements that were already true before load
      this._evaluate();
    }).catch(() => { /* store unavailable (IndexedDB, or unauthenticated server) — proceed without persistence */ });

    // Subscribe to BB writes; re-evaluate on any change
    const listener: Listener = () => this._evaluate();
    this._bb.listenAll(listener);
    this._unsubBb = () => this._bb.removeGlobalListener(listener);
  }

  private _evaluate(): void {
    for (const [id, ach] of this._achievements) {
      if (this._unlocked.has(id)) continue;
      const result = evaluate(ach.cond_ast, this._bb);
      const ternary = result instanceof Ternary ? result : Ternary.from(result);
      if (ternary.isTrue) {
        this._unlock(id, ach);
      }
    }
  }

  private _unlock(id: string, ach: AchievementDef): void {
    this._unlocked.add(id);
    this._bb.set(`_achievement_${id}`, tagValue(true));
    void this._store.markUnlocked(this._gameId, id);
    this._bus.emit("achievement.unlock", {
      id,
      title: ach.title,
      desc: ach.desc,
      icon: ach.icon,
    });
  }

  getUnlocked(): Set<string> {
    return new Set(this._unlocked);
  }

  destroy(): void {
    if (this._unsubBb) { this._unsubBb(); this._unsubBb = null; }
    this._achievements.clear();
    this._unlocked.clear();
  }
}
